import React, { useEffect, useState } from "react";
import { Button } from "src/business-types/grocery/components/Button";
import Image from "src/shared/Image";
import { useConfigurationState } from "./hooks/useConfigurationState";
import { toast } from "react-toastify";
import { PageTitle } from "../PageTitle";

const MAX_SIZE = 5 * 1024 * 1024;
const VALID_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

const validateFile = (file: File | null) => {
  if (!file) return true;
  if (file.size > MAX_SIZE) {
    toast.error("Max 5MB allowed");
    return false;
  }
  if (!VALID_TYPES.includes(file.type)) {
    toast.error("Invalid file type");
    return false;
  }
  return true;
};

const QRUploadCard = ({ 
  title, 
  qr, 
  inputRef, 
  setQR, 
  removeQR, 
  isSaving, 
  handleUpload 
}: any) => {
  const handleRemove = () => {
    // Clear the file input value
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    // Call the remove function
    removeQR();
  };

  return (
    <div className="border p-6 rounded-xl bg-white shadow">
      <h2 className="text-xl font-semibold mb-4 text-center">{title}</h2>

      <div
        onClick={() => !isSaving && inputRef.current?.click()}
        className="w-48 h-48 mx-auto border-2 border-dashed rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-500 bg-gray-50 relative overflow-hidden"
      >
        {qr.preview ? (
          <>
            <Image className="w-full h-full object-contain p-2" imagePath={qr.preview} />
            <div className="absolute inset-0 hover:bg-black/10 flex items-center justify-center">
              <span className="text-white bg-black/50 px-2 py-1 rounded text-xs opacity-0 hover:opacity-100">
                Change
              </span>
            </div>
          </>
        ) : (
          <div className="text-center">
            <span className="text-3xl">{title.includes("Contact") ? "📷" : "📍"}</span>
            <p className="text-xs text-gray-500 mt-2">Click to upload (Max 5MB)</p>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={VALID_TYPES.join(",")}
          onChange={(e) => handleUpload(e, setQR)}
          className="hidden"
        />
      </div>

      {qr.preview && (
        <div className="flex justify-center mt-4">
          <Button 
            label="Remove" 
            variant="danger" 
            onClick={handleRemove} 
            disabled={isSaving} 
          />
        </div>
      )}

      {qr.file && (
        <p className="text-xs text-center mt-2 text-gray-500">
          {qr.file.name} ({(qr.file.size / 1024).toFixed(1)} KB)
        </p>
      )}
    </div>
  );
};

const QRCodesTab = () => {
  const {
    user,
    config,
    contactInputRef,
    locationInputRef,
    contactQR,
    locationQR,
    handleSimpleImageUpload,
    removeContactQR,
    removeLocationQR,
    setContactQR,
    setLocationQR,
    saveQRCodes,
  } = useConfigurationState();

  const [initial, setInitial] = useState({ c: "", l: "" });
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setInitial({ 
      c: config?.contactQrCode || "", 
      l: config?.locationQrCode || "" 
    });
  }, [config?.contactQrCode, config?.locationQrCode]);

const isChanged =
  contactQR.preview !== initial.c ||
  locationQR.preview !== initial.l ||
  !!contactQR.file ||
  !!locationQR.file;

  const handleUpload = (e: any, setter: any) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (validateFile(file)) {
      handleSimpleImageUpload(e, setter);
    } else {
      e.target.value = "";
    }
  };

  const handleSave = async () => {
    if ((contactQR.file && !validateFile(contactQR.file)) || 
        (locationQR.file && !validateFile(locationQR.file))) {
      return;
    }

    setIsSaving(true);
    try {
      await saveQRCodes({ contactQR, locationQR });
      // toast.success("Saved successfully!");
      // Update initial state after successful save
      setInitial({ 
        c: contactQR.preview, 
        l: locationQR.preview 
      });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Save failed");
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (user?.businessType !== "Grocery") {
      document.title = "QR Code › Configuration › iMetanic";
    }
  }, [user]);

  return (
    <div className="w-full mt-4 space-y-6 bg-white p-6 rounded-2xl">
      <PageTitle title=" - QR Codes › Configuration" />

      <div className="grid md:grid-cols-2 gap-6">
        <QRUploadCard
          title="Contact QR Code"
          qr={contactQR}
          inputRef={contactInputRef}
          setQR={setContactQR}
          removeQR={removeContactQR}
          isSaving={isSaving}
          handleUpload={handleUpload}
        />

        <QRUploadCard
          title="Location QR Code"
          qr={locationQR}
          inputRef={locationInputRef}
          setQR={setLocationQR}
          removeQR={removeLocationQR}
          isSaving={isSaving}
          handleUpload={handleUpload}
        />
      </div>

      <div className="flex justify-center">
        <Button
          label={isSaving ? "Saving..." : "Save QR Codes"}
          onClick={handleSave}
          disabled={isSaving || !isChanged}
        />
      </div>
    </div>
  );
};

export default QRCodesTab;